<app-logo-area
  [breadcrumbs]="[
    {'display': 'Content Platform', commands: null},
    {'display': 'Aspera Connectors', commands: ['/o', organization()?.id, 'aspera-endpoints']}]"></app-logo-area>
<main>

  <app-headline-section [title]="title()">
  </app-headline-section>

  @if (error()) {
    <section class="cl-section cl-section-regular">
      <div class="container"><div class="row"><div class="col-12">
        <p class="cl-p-regular cl-color-brick">Could not load this endpoint: {{ error() }}</p>
      </div></div></div>
    </section>
  }

  <!-- ============================================================ endpoint -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row"><div class="col-12">
        <h3 class="cl-h3">Endpoint</h3>
        <table class="cl-table-data">
          <tbody>
            @if (loading()) {
              <tr class="cl-tr-no-data"><td colspan="100"><div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div></td></tr>
            } @else if (endpoint(); as e) {
              <tr><td>Server</td><td>{{ e.server }}</td></tr>
              <tr><td>Share</td><td>{{ e.shareId ?? 'None: a direct Aspera server' }}</td></tr>
              <tr><td>Exports from</td><td>{{ e.upload?.source || 'This endpoint takes no exports in this environment.' }}</td></tr>
              <tr><td>Delivers below</td><td>{{ e.upload?.remoteRoot || '—' }}</td></tr>
              <tr><td>Fixed egress IP</td><td>{{ e.fixedEgressIp || 'None: transfers leave from any address' }}</td></tr>
              @if (worker(); as w) {
                <tr>
                  <td>Worker</td>
                  <td>
                    <span class="it-worker-state"
                          [class.cl-color-leaf]="w.state === 'READY'"
                          [class.cl-color-sea]="w.state === 'BUSY'"
                          [class.cl-color-honey]="w.state === 'STARTING'">{{ w.state | titlecase }}</span>
                    @if (w.since) { <small class="cl-faded">since <app-date [value]="w.since" /></small> }
                    @if (w.holdUntil) { <small class="cl-faded">· held until <app-date [value]="w.holdUntil" /></small> }
                    @if (w.heldBy) { <small class="cl-faded">· {{ w.heldBy }}</small> }
                    @if (w.message) { <small class="cl-faded">— {{ w.message }}</small> }
                  </td>
                </tr>
              }
              <tr>
                <td>Login</td>
                <td>
                  @if (e.status.loggedIn) {
                    <span class="cl-color-sea">Stored</span>
                  } @else if (e.status.needsMfa) {
                    <span class="cl-color-sun">MFA required — an operator must log in</span>
                  } @else {
                    <span class="cl-color-brick">No login stored</span>
                  }
                  @if (e.status.message) {
                    <small class="cl-faded">— {{ e.status.message }}</small>
                  }
                </td>
              </tr>
              <tr><td>Account</td><td>{{ e.status.username || '—' }}</td></tr>
            } @else if (!error()) {
              <tr class="cl-tr-no-data"><td colspan="100">This endpoint does not exist.</td></tr>
            }
          </tbody>
        </table>

        @if (worker() && ('contentplatform:aspera:Mutation/StartWorker' | isAllowedAnywhere: organization())) {
          <!-- A fixed-IP worker starts in minutes; starting it ahead keeps it 30 minutes for browse, login and sends. -->
          <div class="cl-spacing-box">
            <app-do-button #workerButton [messageField]="workerMessage" (actionTriggered)="startWorker($event)"
                           [disabled]="worker()?.state !== 'OFF'">Start worker</app-do-button>
            <app-message-field #workerMessage></app-message-field>
          </div>
        }
                @if (endpoint() && ('contentplatform:aspera:Mutation/Login' | isAllowedAnywhere: organization())) {
          <!-- As signiant's portal login: the login is tried against the share and stored in the endpoint's secret
               only when the share accepts it. One line: the button holds the right edge, the fields fill the rest. -->
          <div class="login-box cl-form">
            <div class="login-row">
              @if (needsCredentials()) {
                <input type="text" autocomplete="username" placeholder="username" [(ngModel)]="username">
                <input type="password" autocomplete="current-password" placeholder="password"
                       [(ngModel)]="password" (keyup.enter)="loginByEnter()">
              }
              @if (mfaPending()) {
                <input type="text" class="cl-field-monospace" autocomplete="one-time-code"
                       placeholder="MFA code" [(ngModel)]="mfaCode" (keyup.enter)="submitMfa(mfaButton)">
                <app-do-button #mfaButton [messageField]="loginMessage"
                               (actionTriggered)="submitMfa($event)">Confirm</app-do-button>
              } @else {
                <app-do-button #loginButton [messageField]="loginMessage" (actionTriggered)="login($event)">
                  {{ endpoint()?.status?.loggedIn && !needsCredentials() ? 'Change login' : 'Log in' }}
                </app-do-button>
              }
            </div>
            <app-message-field #loginMessage></app-message-field>
            @if (loginChecking()) {
              <p class="cl-p-small">{{ workerNote() ?? 'Checking the login from the fixed-address instance.' }}</p>
            }
          </div>
        }
      </div></div>
    </div>
  </section>

  <!-- ============================================================ transfers -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row"><div class="col-12">
        <h3 class="cl-h3">Recent transfers</h3>
        <p>The {{ processLimit }} most recent transfers of this endpoint; older ones stay in the process store.</p>
        <div class="cl-table-scroll">
          <table class="cl-table-data">
            <thead>
              <tr>
                <th class="cl-table-head">File on the share</th>
                <th class="cl-table-head">State</th>
                <th class="cl-table-head">Message</th>
                <th class="cl-table-head">Started</th>
                <th class="cl-table-head">Ended</th>
              </tr>
            </thead>
            <tbody>
              @if (processesLoading()) {
                <tr class="cl-tr-no-data"><td colspan="100"><div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div></td></tr>
              }
              @if (processesError()) {
                <tr class="cl-tr-no-data"><td colspan="100" class="cl-color-brick">Could not load the transfers: {{ processesError() }}</td></tr>
              }
              @for (process of processes(); track process.id) {
                <tr>
                  <td>
                    {{ process.remotePath || '—' }}
                    <p>{{ process.sourceUri }}</p>
                    @if (process.egressIp) {
                      <p>from {{ process.egressIp }}</p>
                    }
                  </td>
                  <td>
                    @if (process.state === 'SUCCESS') {
                      <span class="cl-badge cl-badge-sm cl-color-leaf">Success</span>
                    } @else if (process.state === 'FAILED') {
                      <span class="cl-badge cl-badge-sm cl-color-brick">Failed</span>
                    } @else {
                      <span class="cl-badge cl-badge-sm cl-color-honey">In progress{{ process.percent !== null ? ' ' + process.percent + '%' : '' }}</span>
                    }
                  </td>
                  <td>{{ process.message || '—' }}</td>
                  <td class="cl-td-time"><app-date [value]="process.startDate" empty="—" /></td>
                  <td class="cl-td-time"><app-date [value]="process.endDate" empty="—" /></td>
                </tr>
              }
              @if (!processesLoading() && processes().length === 0 && !processesError()) {
                <tr class="cl-tr-no-data"><td colspan="100">Nothing has been sent to this endpoint yet.</td></tr>
              }
            </tbody>
          </table>
        </div>
      </div></div>
    </div>
  </section>

  <!-- ============================================================ files -->
  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row"><div class="col-12">
        <h3 class="cl-h3">Files on the share</h3>
        @if (folder(); as current) {
          <p>
            <strong>{{ current }}</strong>
            @if (current !== '/') {
              · <a href="" (click)="$event.preventDefault(); up()">up one folder</a>
            }
          </p>
        }
        @if (endpoint()?.browseAsync && browsing()) {
          <p class="cl-p-regular">{{ workerNote() ?? 'Listing on the fixed-address instance.' }}</p>
        }
        @if (browseError()) {
          <p class="cl-p-regular cl-color-brick">Could not list this folder: {{ browseError() }}</p>
        }
        <div class="cl-table-scroll">
          <table class="cl-table-data">
            <thead>
              <tr>
                <th class="cl-table-head">Name</th>
                <th class="cl-table-head">Type</th>
              </tr>
            </thead>
            <tbody>
              @if (browsing()) {
                <tr class="cl-tr-no-data"><td colspan="100"><div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div></td></tr>
              }
              @for (entry of entries(); track entry.path) {
                <tr>
                  <td>
                    @if (entry.type === 'directory') {
                      <a href="" (click)="$event.preventDefault(); open(entry.path)">{{ entry.name }}/</a>
                    } @else {
                      {{ entry.name }}
                    }
                  </td>
                  <td>{{ entry.type === 'directory' ? 'Folder' : 'File' }}</td>
                </tr>
              }
              @if (!browsing() && folder() !== null && entries().length === 0 && !browseError()) {
                <tr class="cl-tr-no-data"><td colspan="100">This folder is empty.</td></tr>
              }
              @if (!loading() && folder() === null && !error()) {
                <tr class="cl-tr-no-data"><td colspan="100">This endpoint is not used in this environment, so it is not browsed here.</td></tr>
              }
            </tbody>
          </table>
        </div>
      </div></div>
    </div>
  </section>
</main>
